import EditIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Edit.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { handleErrorToast } from 'src/components/molecules';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import { CaseDocumentationType } from 'src/graphql';
import { UseCaseDocuments } from 'src/hooks';
import {
  getScanStatusErrorMessage,
  getWorstScanStatus,
  shouldBlockDownload,
} from 'src/utils';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import * as styles from './CaseWorkspaceTable.styles';

interface CaseWorkspaceTableProps {
  caseDocuments: Omit<
    UseCaseDocuments,
    'activeTab' | 'setTempActiveTab' | 'selectActiveTab'
  >;
  documents: CaseDocumentationType[];
  selectedDocuments: CaseDocumentationType[];
  setSelectedDocuments: (documents: CaseDocumentationType[]) => void;
  openDocument: (document: CaseDocumentationType) => void;
  onOpenEditor: () => void;
  setWorkspaceReadOnly: (isReadOnly: boolean) => void;
}

export const CaseWorkspaceTable: FC<CaseWorkspaceTableProps> = ({
  caseDocuments,
  documents,
  setSelectedDocuments,
  openDocument,
  onOpenEditor,
  setWorkspaceReadOnly,
}: CaseWorkspaceTableProps) => {
  const reduxCase = useSelector(casePageVm);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { presets, tokens } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const color = tokens.semantic.color;

  const userHasEditAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  const columns = [
    { label: 'File name', value: 'fileName' },
    {
      label: 'Virus scan status',
      value: 'scanStatus',
    },
    { label: 'Owner', value: 'owner' },
    { label: 'Last action', value: 'lastAction' },
    ...(userHasEditAccess ? [{ label: 'Actions', value: 'actions' }] : []),
  ];

  const rows = documents.map((document) => ({
    document,
    id: document.id,
    fileName: document.title ?? 'Unknown',
    owner: document.Author.name ?? 'Unknown',
    lastAction: formatDate(
      document.lastEdited,
      DateFormats.DD_MM_YYYY_TIME_SHORT,
    ),
    scanStatus: document.Uploads?.length
      ? getWorstScanStatus(document.Uploads)
      : null,
  }));

  const handleOpenDocument = (document: CaseDocumentationType) => {
    const isWorkspaceDocument = !document.s3path;
    if (isWorkspaceDocument) {
      openDocument(document);
      onOpenEditor();
      return;
    }

    if (document.Uploads?.length) {
      const scanStatus = getWorstScanStatus(document.Uploads);

      if (shouldBlockDownload(scanStatus)) {
        handleErrorToast({
          toastMessage: 'Download blocked',
          toastDescription: getScanStatusErrorMessage(scanStatus),
        });
        return;
      }
    }

    openDocument(document);
  };

  return (
    <>
      {rows.length > 0 && (
        <Box data-testid="case-workspace-table">
          <TableContainer sx={styles.tableContainer}>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.headerRow}>
                  {columns.map((col) => (
                    <TableCell key={col.value} sx={styles.headerCell}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  return (
                    <TableRow
                      key={row.id}
                      sx={styles.row}
                      data-testid={`document-${row.id}-row`}
                    >
                      <TableCell
                        sx={styles.clickableCell}
                        onClick={() => {
                          setWorkspaceReadOnly(true);
                          handleOpenDocument(row.document);
                        }}
                      >
                        {row.fileName}
                      </TableCell>
                      <TableCell sx={styles.cell}>{row.scanStatus}</TableCell>
                      <TableCell sx={styles.cell}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Avatar
                            {...getAvatarProps({
                              size: 'medium',
                              color: 'red',
                            })}
                          >
                            {getInitials(row.owner)}
                          </Avatar>
                          {row.owner}
                        </Box>
                      </TableCell>
                      <TableCell sx={styles.cell}>{row.lastAction}</TableCell>
                      {userHasEditAccess && (
                        <TableCell sx={styles.actionCell}>
                          {!row.document.s3path && (
                            <IconButton
                              size="small"
                              aria-label={`Edit ${row.fileName}`}
                              onClick={() => {
                                setWorkspaceReadOnly(false);
                                handleOpenDocument(row.document);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            aria-label={`Delete ${row.fileName}`}
                            onClick={() => {
                              setSelectedDocuments([row.document]);
                              setShowConfirmDialog(true);
                            }}
                          >
                            <TrashIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Dialog
        open={showConfirmDialog}
        onClose={() => {
          setSelectedDocuments([]);
          setShowConfirmDialog(false);
        }}
      >
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete this file. <br /> If you continue, all
            changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            data-testid="cancel-remove-document-confirm"
            variant="outlined"
            size="medium"
            onClick={() => {
              setSelectedDocuments([]);
              setShowConfirmDialog(false);
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-remove-documents"
            sx={{
              backgroundColor: color.action.destructive.default.value,
              color: color.action.destructive.onDestructive.value,
              '&:not(.Mui-disabled):hover': {
                backgroundColor: color.action.destructive.hover.value,
              },
            }}
            variant="contained"
            size="medium"
            onClick={() => {
              caseDocuments.handleRemoveDocuments();
              setShowConfirmDialog(false);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
